import { Button, Link, Tooltip } from "@mui/material";
import { CarbonDataGrid } from "portal/components/CarbonDataGrid";
import { DateTime } from "luxon";
import { debounce } from "portal/utils/timing";
import { ExpandedImage } from "portal/components/images/ExpandedImage";
import {
  getImageDate,
  getImageLocation,
  splitS3Url,
} from "portal/utils/images";
import { getLocaleCropName } from "portal/utils/crops";
import { GridColDef, GridPaginationModel } from "@mui/x-data-grid-premium";
import { Image } from "protos/portal/veselka";
import { imageServiceApi } from "portal/state/imageServiceApi";
import { NoScroll } from "portal/components/Page";
import { portalApi } from "portal/state/portalApi";
import { portalLanguageOrDefault } from "portal/i18nConstants";
import { skipToken } from "@reduxjs/toolkit/query";
import { titleCase } from "portal/utils/strings";
import { useLazyPopups, useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import React, { FunctionComponent, useEffect, useMemo, useState } from "react";

function useImageServiceSrc(s3Url: string | undefined): {
  src: string | undefined;
  isSrcLoading: boolean;
  isSrcError: boolean;
} {
  const imageRequest = useMemo(() => {
    if (!s3Url) {
      return;
    }
    try {
      return splitS3Url(s3Url);
    } catch {
      // invalid S3 URL
    }
  }, [s3Url]);

  const {
    data: src,
    isLoading,
    error,
  } = imageServiceApi.endpoints.getFullImage.useQuery(
    imageRequest ?? skipToken
  );

  return {
    src,
    isSrcLoading: isLoading && !src,
    isSrcError: Boolean(error),
  };
}

const defaultColumn: Partial<GridColDef> = {
  sortable: false,
  disableColumnMenu: true,
};

const LIMIT = 100;

const _RobotUploads: FunctionComponent = () => {
  const { serial } = useParams();
  const { t, i18n } = useTranslation();
  const language = portalLanguageOrDefault(i18n.language);

  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 1,
    pageSize: LIMIT,
  });
  const [uploads, setUploads] = useState<Image[]>([]);
  const [listUploads, { isLoading, isSuccess }] = useLazyPopups(
    portalApi.endpoints.listUploads.useLazyQuery()
  );
  const { data: globalCrops } = useQueryPopups(
    portalApi.endpoints.listCrops.useQuery()
  );

  useEffect(() => {
    if (!serial) {
      return;
    }
    const initialFetch = async (): Promise<void> => {
      const data = await listUploads(
        { serial, pagination: { page: 1, limit: LIMIT } },
        true
      );
      setUploads(data.data ?? []);
    };
    initialFetch();
  }, [serial]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadIndexById = useMemo(() => {
    const map: Record<string, number> = {};
    for (const [index, image] of uploads.entries()) {
      map[image.id] = index;
    }
    return map;
  }, [uploads]);
  const [lightboxIndex, setLightboxIndex] = useState<number | undefined>();
  const lightbox =
    lightboxIndex === undefined ? undefined : uploads[lightboxIndex];
  const { src, isSrcLoading, isSrcError } = useImageServiceSrc(lightbox?.url);

  const onNext =
    lightboxIndex !== undefined && lightboxIndex < uploads.length - 1
      ? () => setLightboxIndex(lightboxIndex + 1)
      : undefined;
  const onBack =
    lightboxIndex !== undefined && lightboxIndex > 0
      ? () => setLightboxIndex(lightboxIndex - 1)
      : undefined;

  const columns: GridColDef<Image>[] = [
    {
      ...defaultColumn,
      field: "capturedAt",
      headerName: t("models.images.fields.capturedAt"),
      renderCell: ({ row: image }) => {
        const date = getImageDate(image);
        if (!date) {
          return t("utils.descriptors.unknown");
        }
        return (
          <Tooltip
            arrow
            title={date.toLocaleString(DateTime.DATETIME_FULL, {
              locale: i18n.language,
            })}
          >
            <span>{date.toRelative({ locale: i18n.language })}</span>
          </Tooltip>
        );
      },
    },
    {
      ...defaultColumn,
      field: "cropId",
      headerName: titleCase(t("models.crops.crop_one")),
      renderCell: ({ row: image }) => {
        const cropId = image.cropId;
        const cropDisplayName =
          globalCrops && globalCrops[cropId]
            ? getLocaleCropName(globalCrops[cropId], language)
            : image.crop;
        return <span>{cropDisplayName}</span>;
      },
    },
    {
      ...defaultColumn,
      field: "geoJson",
      headerName: t("models.images.fields.geoJson"),
      width: 200,
      valueGetter: (value, image) => {
        const [geohash] = getImageLocation(image);
        return geohash;
      },
      cellClassName: "cursor-pointer underline text-blue-500",
      renderCell: ({ row: image }) => {
        const [geohash, point] = getImageLocation(image);
        if (!point) {
          return geohash;
        }
        return (
          <Link
            href={`https://www.google.com/maps/search/${point.join(",")}`}
            target="_blank"
            color="secondary"
            className="font-mono text-sm"
          >
            {geohash}
          </Link>
        );
      },
    },
    {
      ...defaultColumn,
      field: "camera",
      width: 150,
      headerName: t("models.images.fields.camera"),
      valueGetter: (value, image) => {
        const rowName = titleCase(image.rowId).replace(/(\d+)$/, " $1");
        let camera = image.camId;
        if (camera) {
          camera = titleCase(camera).replace(/(\d+)$/, " $1");
        }
        const rowPrefix = rowName ? `${rowName} - ` : "";
        return `${rowPrefix}${camera}`;
      },
    },
    {
      ...defaultColumn,
      field: "url",
      headerName: titleCase(t("models.images.image_one")),
      renderCell: ({ row: image }) => (
        <Button
          variant="text"
          onClick={() => setLightboxIndex(uploadIndexById[image.id])}
          color="secondary"
        >
          {t("models.images.fields.url")}
        </Button>
      ),
    },
  ];

  if (isSuccess && uploads.length === 0) {
    return (
      <div className="flex flex-col h-full w-full items-center justify-center">
        <div className="text-lighten-400 flex items-center gap-2">
          {t("views.fleet.robots.uploads.errors.empty")}
        </div>
      </div>
    );
  }

  return (
    <NoScroll>
      <CarbonDataGrid<Image>
        rows={uploads}
        getRowId={(row) => row.id}
        columns={columns}
        disableRowSelectionOnClick
        loading={isLoading}
        initialState={{
          sorting: { sortModel: [{ field: "capturedAt", sort: "desc" }] },
        }}
        paginationModel={pagination}
        onPaginationModelChange={debounce(async ({ page, pageSize }) => {
          setPagination({ page, pageSize });
          if (!serial || !pageSize) {
            return;
          }
          if ((page + 1) * pageSize >= uploads.length) {
            const nextServerPage = Math.floor(uploads.length / LIMIT) + 1;
            const data = await listUploads(
              {
                serial,
                pagination: { page: nextServerPage, limit: LIMIT },
              },
              true
            );
            setUploads([...uploads, ...(data.data ?? [])]);
          }
        })}
        pagination
        autoPageSize
      />
      <ExpandedImage
        open={Boolean(lightbox)}
        onClose={() => setLightboxIndex(undefined)}
        src={src}
        isSrcLoading={isSrcLoading}
        isSrcError={isSrcError}
        onNext={onNext}
        onBack={onBack}
      />
    </NoScroll>
  );
};

export const RobotUploads = withAuthenticationRequired(_RobotUploads);
